import { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Image as ImageIcon, Download, Send, CheckCircle2, MessageSquare } from 'lucide-react';

const STATUS_STYLES = {
  open: 'bg-green-100 text-green-700',
  applications_closed: 'bg-amber-100 text-amber-700',
  assigned: 'bg-blue-100 text-blue-700',
  completed: 'bg-gray-100 text-gray-600',
};

const PartnerMarketplaceProject = () => {
  const { id } = useParams();
  const { getAuthHeaders, API_URL } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ cover_note: '', relevant_experience: '', proposed_timeline: '' });
  const [attachment, setAttachment] = useState(null);
  const [question, setQuestion] = useState('');

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/projects/${id}`, { headers: getAuthHeaders() });
      setData(res.data);
    } catch (error) {
      console.error('Failed to load project:', error);
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  };

  const downloadFile = async (file) => {
    try {
      const res = await axios.get(`${API_URL}/api/marketplace/files/${file.file_id}`, {
        headers: getAuthHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.filename || 'download';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!form.cover_note.trim()) {
      toast.error('Please add a cover note');
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('cover_note', form.cover_note);
      fd.append('relevant_experience', form.relevant_experience);
      fd.append('proposed_timeline', form.proposed_timeline);
      if (attachment) fd.append('attachment', attachment);
      await axios.post(`${API_URL}/api/marketplace/projects/${id}/apply`, fd, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Application submitted!');
      setForm({ cover_note: '', relevant_experience: '', proposed_timeline: '' });
      setAttachment(null);
      fetchProject();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePostQuestion = async (e) => {
    e.preventDefault();
    if (!question.trim()) return;
    try {
      await axios.post(`${API_URL}/api/marketplace/projects/${id}/comments`, { body: question }, { headers: getAuthHeaders() });
      setQuestion('');
      toast.success('Question posted');
      fetchProject();
    } catch (error) {
      toast.error('Failed to post question');
    }
  };

  if (loading) {
    return <DashboardLayout><div className="text-center py-12 text-[#64748B]">Loading...</div></DashboardLayout>;
  }
  if (!data) {
    return <DashboardLayout><div className="text-center py-12 text-[#64748B]">Project not found.</div></DashboardLayout>;
  }

  const { project, my_application, comments } = data;
  const canApply = project.status === 'open' && !my_application;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl">
        <button onClick={() => navigate('/marketplace')} className="flex items-center gap-1.5 text-sm text-[#64748B] hover:text-[#1A1A1A]">
          <ArrowLeft className="w-4 h-4" /> Back to marketplace
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-2xl font-bold text-[#1A1A1A]">{project.title}</h2>
            {project.customer && <p className="text-[#64748B] text-sm mt-1">{project.customer}{project.vertical ? ` · ${project.vertical}` : ''}</p>}
          </div>
          <span className={`px-2.5 py-1 rounded-full text-xs font-medium capitalize shrink-0 ${STATUS_STYLES[project.status]}`}>
            {String(project.status).replace(/_/g, ' ')}
          </span>
        </div>

        {my_application && (
          <Card className="border-l-4 border-l-[#ef785b]">
            <CardContent className="p-4 flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-[#ef785b]" />
              <div>
                <p className="text-sm font-medium text-[#1A1A1A]">
                  You applied to this project — status: <span className="capitalize">{my_application.status}</span>
                </p>
                {my_application.decision_reason && <p className="text-xs text-[#64748B] mt-0.5">{my_application.decision_reason}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Details */}
        <Card>
          <CardHeader><CardTitle className="text-base">Project Brief</CardTitle></CardHeader>
          <CardContent className="space-y-4 text-sm text-[#475569]">
            {project.description && <p className="whitespace-pre-wrap">{project.description}</p>}
            {project.brief_text && (
              <div><p className="font-medium text-[#1A1A1A] mb-1">Brief</p><p className="whitespace-pre-wrap">{project.brief_text}</p></div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              {project.scope && <Detail label="Scope" value={project.scope} />}
              {project.timeline && <Detail label="Expected timeline" value={project.timeline} />}
              {project.deliverables && <Detail label="Deliverables" value={project.deliverables} />}
              {project.budget && <Detail label="Budget / Commission" value={project.budget} />}
              {project.commission_note && <Detail label="Commission note" value={project.commission_note} />}
            </div>
            {(project.skills || []).length > 0 && (
              <div>
                <p className="font-medium text-[#1A1A1A] mb-1.5">Required skills</p>
                <div className="flex flex-wrap gap-1.5">
                  {project.skills.map((s) => <Badge key={s} variant="secondary" className="font-normal">{s}</Badge>)}
                </div>
              </div>
            )}
            {/* Downloads */}
            {(project.brief_file || project.blueprint_file) && (
              <div className="flex flex-wrap gap-2 pt-2">
                {project.brief_file && (
                  <Button variant="outline" size="sm" onClick={() => downloadFile(project.brief_file)}>
                    <FileText className="w-4 h-4 mr-1.5" /> Brief file <Download className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                )}
                {project.blueprint_file && (
                  <Button variant="outline" size="sm" onClick={() => downloadFile(project.blueprint_file)}>
                    <ImageIcon className="w-4 h-4 mr-1.5" /> Blueprint <Download className="w-3.5 h-3.5 ml-1.5" />
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Application form */}
        {canApply ? (
          <Card>
            <CardHeader><CardTitle className="text-base">Apply for this project</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleApply} className="space-y-4">
                <div>
                  <Label htmlFor="cover_note">Cover note *</Label>
                  <Textarea id="cover_note" rows={4} value={form.cover_note}
                    onChange={(e) => setForm({ ...form, cover_note: e.target.value })}
                    placeholder="Why are you a great fit for this project?" />
                </div>
                <div>
                  <Label htmlFor="experience">Relevant experience</Label>
                  <Textarea id="experience" rows={3} value={form.relevant_experience}
                    onChange={(e) => setForm({ ...form, relevant_experience: e.target.value })}
                    placeholder="Similar projects you've delivered" />
                </div>
                <div>
                  <Label htmlFor="timeline">Proposed timeline</Label>
                  <Input id="timeline" value={form.proposed_timeline}
                    onChange={(e) => setForm({ ...form, proposed_timeline: e.target.value })}
                    placeholder="e.g. 4 weeks" />
                </div>
                <div>
                  <Label htmlFor="attachment">Attachment (optional — PDF, DOCX, PNG, JPEG)</Label>
                  <Input id="attachment" type="file" accept=".pdf,.docx,.doc,.png,.jpg,.jpeg"
                    onChange={(e) => setAttachment(e.target.files[0])} />
                </div>
                <Button type="submit" disabled={submitting} className="bg-[#ef785b] hover:bg-[#e0664a]">
                  <Send className="w-4 h-4 mr-1.5" /> {submitting ? 'Submitting...' : 'Submit application'}
                </Button>
              </form>
            </CardContent>
          </Card>
        ) : !my_application && (
          <Card><CardContent className="py-6 text-center text-[#64748B] text-sm">
            This project is no longer accepting applications.
          </CardContent></Card>
        )}

        {/* Q&A */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Questions &amp; Answers</CardTitle>
            <p className="text-xs text-[#64748B]">Your questions are private between you and the Expedify team.</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <form onSubmit={handlePostQuestion} className="flex gap-2">
              <Input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Ask a question about this project..." />
              <Button type="submit" variant="outline">Post</Button>
            </form>
            {comments.length === 0 ? (
              <p className="text-sm text-[#94A3B8] text-center py-2">No questions yet.</p>
            ) : (
              <div className="space-y-3">
                {comments.map((c) => (
                  <div key={c.id} className={`flex ${c.author_role === 'admin' ? 'justify-start' : 'justify-end'}`}>
                    <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${c.author_role === 'admin' ? 'bg-[#ef785b]/10 text-[#1A1A1A]' : 'bg-[#F1F5F9] text-[#1A1A1A]'}`}>
                      <p className="text-[10px] font-medium mb-0.5 text-[#64748B]">
                        {c.author_role === 'admin' ? 'Expedify Team' : 'You'} · {c.created_at ? new Date(c.created_at).toLocaleString() : ''}
                      </p>
                      <p className="whitespace-pre-wrap">{c.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <p className="font-medium text-[#1A1A1A] mb-0.5">{label}</p>
    <p className="whitespace-pre-wrap">{value}</p>
  </div>
);

export default PartnerMarketplaceProject;
